import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Box,
} from "@mui/material";
import { useGetInvestorsSummaryQuery } from "api/api-invoices";
import { useGetCurrentUserQuery } from "api/api-users-v2";

interface PerformanceProps {
  data?: any;
  masterId?: string;
  userId?: string;
}

const Performance: React.FC<PerformanceProps> = ({ data, masterId, userId }) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  
  // Extract id from current user
  const idToUse = masterId || userId || currentUser?.id;
  
  // Use the data passed as prop if available, otherwise fetch investors summary
  const { data: invoiceData, isLoading } = useGetInvestorsSummaryQuery(
    { year: 'all' },
    {
      skip: !!data || !idToUse, // Skip API call if data is provided as prop or if idToUse is not available
    }
  );

  // Use either the data from props or from the API call
  const activeInvoice = data || invoiceData;

  const yearlyPerformance = useMemo(() => {
    // Return empty if there are no invoices
    if (!activeInvoice?.invoices?.length) return [];

    // Filter out pre-listing invoices to ensure consistent calculation
    const validInvoices = activeInvoice.invoices.filter((invoice: any) => {
      return invoice.phase !== "0_PRE_LISTING" && 
             invoice.phase !== "PRE_LISTING" && 
             invoice.status !== "pre_listing" &&
             invoice.status !== "prelisting";
    });

    // Group by year using only finance_date and days_financed
    // No fallback logic for days_financed; only use what's present
    const yearMap: Record<string, { weightedPrincipal: number; totalInterest: number }> = {};
    const currentYear = new Date().getFullYear();
    yearMap[currentYear.toString()] = { weightedPrincipal: 0, totalInterest: 0 };

    validInvoices.forEach((invoice: any) => {
      // Skip if no finance_date (cannot group by year)
      if (!invoice.finance_date) return;
      const year = new Date(invoice.finance_date).getFullYear();
      const principal = invoice.principle_amount || 0;
      const interest = invoice.interest_amount || 0;
      // Only use days_financed as provided, do not calculate from other dates
      const daysFinanced = invoice.days_financed || 0;
      // Calculate weighted principal for this invoice
      const weightedPrincipal = (principal * daysFinanced) / 365;
      if (!yearMap[year]) {
        yearMap[year] = { weightedPrincipal: 0, totalInterest: 0 };
      }
      yearMap[year].weightedPrincipal += weightedPrincipal;
      yearMap[year].totalInterest += interest;
    });

    // For each year, calculate the annualized rate of interest
    // Formula: (Total Interest) / (Weighted Principal) * 100
    const yearlyData = Object.entries(yearMap).map(([year, values]) => ({
      year,
      interestRate:
        values.weightedPrincipal > 0
          ? (values.totalInterest / values.weightedPrincipal) * 100
          : 0,
    }));

    // Sort by year descending and take only last 3 years for display
    return yearlyData
      .sort((a, b) => Number(b.year) - Number(a.year))
      .slice(0, 3);
  }, [activeInvoice]);
  
  if (isLoading) {
    return <Typography variant="h6">Loading performance data...</Typography>;
  }

  if (!idToUse && !data) {
    return (
      <Alert severity="warning">
        No user data available. Please log in again.
      </Alert>
    );
  }

  const formatInterestRate = (rate: number) => {
    const value = parseFloat(rate.toFixed(2));
    const isNegative = value < 0;
    const color = isNegative ? "#f44336" : "#4caf50";
    
    return (
      <Box sx={{ 
        color: color, 
        fontWeight: 'medium',
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%'
      }}>
        {isNegative ? `-${Math.abs(value).toFixed(2)}` : value.toFixed(2)}%
      </Box>
    );
  };

  console.log(yearlyPerformance, "yearlyPerformance");

  return (
    <Card
      sx={{ 
        backgroundColor: "#f0f0f0", 
        padding: 2, 
        height: "100%",
        boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
        borderRadius: 2
      }}
    >
      <CardContent sx={{ padding: 0 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            mb: 2
          }}
        >
          Yearly Performance
        </Typography>

        {yearlyPerformance.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{ 
              backgroundColor: "#fff", 
              boxShadow: "none",
              borderRadius: 1,
              overflow: "hidden" 
            }}
          >
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                {/* <TableRow>
                  <TableCell sx={{ fontWeight: "bold", py: 1.5 }}>Year</TableCell>
                  <TableCell sx={{ fontWeight: "bold", py: 1.5, textAlign: 'right' }}>
                    Interest Earned
                  </TableCell>
                </TableRow> */}
              </TableHead>
              <TableBody>
                {yearlyPerformance.map((row) => (
                  <TableRow key={row.year} sx={{ '&:nth-of-type(even)': { backgroundColor: "#f9f9f9" } }}>
                    <TableCell>{row.year}</TableCell>
                    <TableCell>{formatInterestRate(row.interestRate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body1">No performance data available</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default Performance;
